import React, { useState, useEffect } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, IconButton, Paper, Snackbar, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
  useMediaQuery, useTheme
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Layers as LayersIcon } from '@mui/icons-material';
import { gradingAPI, academicAPI } from '../../services/api';
import { useAccessControl } from '../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../hooks/usePermissions';
import ModulePageHeader from '../common/ModulePageHeader';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';
import AssignmentGradeFormDialog from './forms/AssignmentGradeFormDialog';
import BulkAssignmentGradeFormDialog from './forms/BulkAssignmentGradeFormDialog';
import GradingScaleFormDialog from './forms/GradingScaleFormDialog';

const INITIAL_FORM = { class_id: '', grading_scale_id: '', academic_year: new Date().getFullYear(), is_active: true };
const INITIAL_BULK = { class_ids: [], grading_scale_id: '', academic_year: new Date().getFullYear(), is_active: true };
const INITIAL_SCALE = { scale_name: '', scale_code: '', description: '', is_default: false, is_active: true };

const ClassGradingScales = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.REPORT_CARD.GRADING, ACTIONS.READ);
  const canCreate = can(RESOURCES.REPORT_CARD.GRADING, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.REPORT_CARD.GRADING, ACTIONS.UPDATE);
  const canDelete = can(RESOURCES.REPORT_CARD.GRADING, ACTIONS.DELETE);
  const [assignments, setAssignments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [scales, setScales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingBulk, setSavingBulk] = useState(false);
  const [savingScale, setSavingScale] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [showScaleForm, setShowScaleForm] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [bulkFormData, setBulkFormData] = useState(INITIAL_BULK);
  const [scaleFormData, setScaleFormData] = useState(INITIAL_SCALE);

  useEffect(() => {
    if (canRead) fetchAll();
  }, [canRead]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [aRes, cRes, sRes] = await Promise.all([
        gradingAPI.getClassGradingScales(),
        academicAPI.getClasses(),
        gradingAPI.getActiveGradingScales(),
      ]);
      setAssignments(Array.isArray(aRes.data) ? aRes.data : aRes.data.data || []);
      setClasses(Array.isArray(cRes.data) ? cRes.data : cRes.data.data || []);
      setScales(Array.isArray(sRes.data) ? sRes.data : sRes.data.data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  const fetchAssignments = async () => {
    try {
      const response = await gradingAPI.getClassGradingScales();
      setAssignments(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch { /* silent */ }
  };

  const fetchScales = async () => {
    try {
      const response = await gradingAPI.getActiveGradingScales();
      setScales(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch { /* silent */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingAssignment) {
        await gradingAPI.updateClassGradingScale(editingAssignment.id, formData);
        setSnackbar({ open: true, message: 'Assignment updated successfully', severity: 'success' });
      } else {
        await gradingAPI.assignGradingScale(formData);
        setSnackbar({ open: true, message: 'Grading scale assigned successfully', severity: 'success' });
      }
      fetchAssignments();
      resetForm();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Operation failed', severity: 'error' });
    } finally { setSaving(false); }
  };

  const handleEdit = (a) => { setEditingAssignment(a); setFormData({ class_id: a.class_id, grading_scale_id: a.grading_scale_id, academic_year: a.academic_year, is_active: a.is_active }); setShowForm(true); };
  const resetForm = () => { setFormData(INITIAL_FORM); setEditingAssignment(null); setShowForm(false); };

  const confirmDeleteAction = async () => {
    try {
      await gradingAPI.deleteClassGradingScale(confirmDelete);
      setSnackbar({ open: true, message: 'Assignment deleted successfully', severity: 'success' });
      fetchAssignments();
    } catch { setSnackbar({ open: true, message: 'Failed to delete assignment', severity: 'error' }); }
    finally { setConfirmDelete(null); }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (!bulkFormData.class_ids.length) { setSnackbar({ open: true, message: 'Please select at least one class', severity: 'error' }); return; }
    setSavingBulk(true);
    try {
      await gradingAPI.bulkAssignGradingScale(bulkFormData);
      setSnackbar({ open: true, message: `Grading scale assigned to ${bulkFormData.class_ids.length} classes successfully`, severity: 'success' });
      fetchAssignments();
      setBulkFormData(INITIAL_BULK);
      setShowBulkForm(false);
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Bulk assignment failed', severity: 'error' });
    } finally { setSavingBulk(false); }
  };

  const handleClassToggle = (classId) => setBulkFormData((prev) => ({
    ...prev,
    class_ids: prev.class_ids.includes(classId) ? prev.class_ids.filter((id) => id !== classId) : [...prev.class_ids, classId],
  }));

  const handleScaleSubmit = async (e) => {
    e.preventDefault();
    setSavingScale(true);
    try {
      const response = await gradingAPI.createGradingScale(scaleFormData);
      const created = response?.data?.data || response?.data;
      setSnackbar({ open: true, message: 'Grading scale added successfully', severity: 'success' });
      await fetchScales();
      if (created?.id) setFormData((prev) => ({ ...prev, grading_scale_id: created.id }));
      setScaleFormData(INITIAL_SCALE);
      setShowScaleForm(false);
    } catch (error) {
      setSnackbar({ open: true, message: error?.response?.data?.message || 'Failed to add grading scale', severity: 'error' });
    } finally { setSavingScale(false); }
  };

  if (!canRead) return <Alert severity="error">You do not have permission to view class grading scales.</Alert>;

  return (
    <Box>
      <ModulePageHeader
        title="Class Grading Scales"
        subtitle="Assign grading scales to classes"
        actions={[
          ...(canCreate ? [<Button key="add-scale" variant="outlined" onClick={() => setShowScaleForm(true)} fullWidth={isMobile}>Add Scale</Button>] : []),
          ...(canCreate ? [<Button key="bulk" variant="outlined" startIcon={<LayersIcon />} onClick={() => setShowBulkForm(true)} fullWidth={isMobile}>Bulk Assign</Button>] : []),
          ...(canCreate ? [<Button key="single" variant="contained" startIcon={<AddIcon />} onClick={() => setShowForm(true)} fullWidth={isMobile}>Assign Scale</Button>] : []),
        ]}
      />

      <Card>
        <CardContent>
          {loading ? (
            <Box py={5} textAlign="center"><CircularProgress size={28} /></Box>
          ) : assignments.length === 0 ? (
            <EmptyRecordsState message="No class grading assignments found." />
          ) : isMobile ? (
            <ResponsiveRecordList
              items={assignments}
              renderItem={(a) => (
                <Stack spacing={1.25}>
                  <Typography fontWeight={700}>{a.class?.name || 'Class N/A'}</Typography>
                  <Typography variant="body2" color="text.secondary">{a.grading_scale?.scale_name} ({a.grading_scale?.scale_code})</Typography>
                  <Typography variant="body2" color="text.secondary">Academic Year: {a.academic_year}</Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Chip label={a.is_active ? 'Active' : 'Inactive'} color={a.is_active ? 'success' : 'default'} size="small" />
                    <Box>
                      {canUpdate && <IconButton size="small" color="primary" onClick={() => handleEdit(a)}><EditIcon /></IconButton>}
                      {canDelete && <IconButton size="small" color="error" onClick={() => setConfirmDelete(a.id)}><DeleteIcon /></IconButton>}
                    </Box>
                  </Box>
                </Stack>
              )}
            />
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 780 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Class</TableCell>
                    <TableCell>Grading Scale</TableCell>
                    <TableCell>Academic Year</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignments.map((a) => (
                    <TableRow key={a.id} hover>
                      <TableCell><strong>{a.class?.name}</strong></TableCell>
                      <TableCell>{a.grading_scale?.scale_name} ({a.grading_scale?.scale_code})</TableCell>
                      <TableCell>{a.academic_year}</TableCell>
                      <TableCell><Chip label={a.is_active ? 'Active' : 'Inactive'} color={a.is_active ? 'success' : 'default'} size="small" /></TableCell>
                      <TableCell>
                        {canUpdate && <IconButton size="small" color="primary" onClick={() => handleEdit(a)}><EditIcon /></IconButton>}
                        {canDelete && <IconButton size="small" color="error" onClick={() => setConfirmDelete(a.id)}><DeleteIcon /></IconButton>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete Assignment</DialogTitle>
        <DialogContent><DialogContentText>Are you sure you want to delete this grading scale assignment?</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteAction}>Delete</Button>
        </DialogActions>
      </Dialog>

      <AssignmentGradeFormDialog open={showForm} onClose={resetForm} onSubmit={handleSubmit} editingAssignment={editingAssignment} formData={formData} setFormData={setFormData} classes={classes} scales={scales} saving={saving} isMobile={isMobile} />
      <BulkAssignmentGradeFormDialog open={showBulkForm} onClose={() => { setBulkFormData(INITIAL_BULK); setShowBulkForm(false); }} onSubmit={handleBulkSubmit} bulkFormData={bulkFormData} setBulkFormData={setBulkFormData} classes={classes} scales={scales} onClassToggle={handleClassToggle} saving={savingBulk} isMobile={isMobile} />
      <GradingScaleFormDialog open={showScaleForm} onClose={() => setShowScaleForm(false)} onSubmit={handleScaleSubmit} editingScale={null} formData={scaleFormData} setFormData={setScaleFormData} saving={savingScale} isMobile={isMobile} />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ClassGradingScales;
